<template>
    <div>
        <div class="main-container">
            <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

            <!-- Back nav -->
            <div class="req-back">
                <a class="req-back__link" href="javascript:void(0)"
                    @click="$router.push({ path: '/kids-device', query: { profileId: profileId } })">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Home
                </a>
            </div>

            <!-- Page header -->
            <div class="pm-page-header">
                <div>
                    <h1 class="pm-page-header__title">Payment Methods</h1>
                    <p class="pm-page-header__sub">Manage your saved cards</p>
                </div>
                <button class="pm-add-btn" @click="addPaymentMethod()" :disabled="isAddingCard">
                    <span v-if="isAddingCard"><i class="fa fa-spinner fa-spin"></i></span>
                    <span v-else>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        Add Card
                    </span>
                </button>
            </div>

            <!-- Success banner after Stripe redirect -->
            <div class="pm-success-banner" v-if="showSuccessBanner">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="flex-shrink:0;">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Card added successfully!</span>
                <button class="pm-success-banner__close" @click="showSuccessBanner = false">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>

            <!-- Cancel banner -->
            <div class="pm-cancel-banner" v-if="showCancelBanner">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="flex-shrink:0;">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
                <span>Card setup was cancelled.</span>
                <button class="pm-success-banner__close" @click="showCancelBanner = false">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>

            <!-- Loader -->
            <div class="pm-loader" v-if="isLoading">
                <i class="fa fa-spinner fa-spin"></i>
                <span>Loading payment methods...</span>
            </div>

            <!-- Empty -->
            <div class="pm-empty" v-else-if="!paymentMethods.data || paymentMethods.data.length === 0">
                <div class="pm-empty__icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                        <rect x="1" y="4" width="22" height="16" rx="2" stroke="#d1d5db" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M1 10h22" stroke="#d1d5db" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                </div>
                <span class="pm-empty__title">No payment methods</span>
                <span class="pm-empty__sub">Add a card to manage your subscription</span>
                <button class="pm-add-btn pm-add-btn--full" @click="addPaymentMethod()" :disabled="isAddingCard">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    Add Your First Card
                </button>
            </div>

            <!-- Payment method cards -->
            <div class="pm-list" v-else>
                <div
                    class="pm-card"
                    v-for="pm in sortedPaymentMethods"
                    :key="pm.id"
                    :class="{ 'pm-card--default': pm.id === paymentMethods.default_source }">

                    <!-- Default ribbon -->
                    <div class="pm-default-ribbon" v-if="pm.id === paymentMethods.default_source">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        Default Payment Method
                    </div>
                    <!-- Card header -->
                    <div class="pm-card__header">
                        <!-- Icon based on type -->
                        <div class="pm-card__brand-icon">
                            <!-- Card type -->
                            <span v-if="pm.type === 'card' && getCardBrandLogo(pm.card?.brand)"
                                v-html="getCardBrandLogo(pm.card?.brand)"
                                class="pm-card__brand-svg">
                            </span>

                            <!-- Link (Stripe Link) -->
                            <svg v-else-if="pm.type === 'link'" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="8" fill="#00D66B"/>
                                <text x="24" y="32" text-anchor="middle" font-family="Arial Black,sans-serif" font-size="16" font-weight="900" fill="#fff">Link</text>
                            </svg>

                            <!-- PayPal -->
                            <svg v-else-if="pm.type === 'paypal'" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="8" fill="#003087"/>
                                <text x="24" y="32" text-anchor="middle" font-family="Arial Black,sans-serif" font-size="14" font-weight="900" fill="#fff">PayPal</text>
                            </svg>

                            <!-- SEPA -->
                            <svg v-else-if="pm.type === 'sepa_debit'" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="8" fill="#1a56db"/>
                                <text x="24" y="32" text-anchor="middle" font-family="Arial,sans-serif" font-size="13" font-weight="700" fill="#fff">SEPA</text>
                            </svg>

                            <!-- BACS -->
                            <svg v-else-if="pm.type === 'bacs_debit'" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="8" fill="#0f172a"/>
                                <text x="24" y="32" text-anchor="middle" font-family="Arial,sans-serif" font-size="13" font-weight="700" fill="#fff">BACS</text>
                            </svg>

                            <!-- Apple Pay -->
                            <svg v-else-if="pm.type === 'apple_pay'" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="8" fill="#000"/>
                                <text x="24" y="32" text-anchor="middle" font-family="Arial,sans-serif" font-size="11" font-weight="600" fill="#fff"> Pay</text>
                            </svg>

                            <!-- Google Pay -->
                            <svg v-else-if="pm.type === 'google_pay'" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="8" fill="#fff" stroke="#e5e7eb"/>
                                <text x="24" y="30" text-anchor="middle" font-family="Arial,sans-serif" font-size="10" font-weight="600" fill="#4285f4">Google</text>
                                <text x="24" y="42" text-anchor="middle" font-family="Arial,sans-serif" font-size="10" font-weight="600" fill="#34a853">Pay</text>
                            </svg>

                            <!-- Fallback generic -->
                            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <rect x="1" y="4" width="22" height="16" rx="2" stroke="currentColor" stroke-width="1.5"/>
                                <path d="M1 10h22" stroke="currentColor" stroke-width="1.5"/>
                            </svg>
                        </div>

                        <div class="pm-card__info">
                            <div class="pm-card__name-row">
                                <span class="pm-card__name">{{ getPaymentMethodName(pm) }}</span>
                                <span class="pm-default-badge" v-if="pm.id === paymentMethods.default_source">
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                    </svg>
                                    Default
                                </span>
                            </div>
                            <span class="pm-card__expiry">{{ getPaymentMethodSubtitle(pm) }}</span>
                        </div>
                    </div>

                    <!-- Billing details -->
                    <div class="pm-card__billing" v-if="pm.billing_details && (pm.billing_details.name || pm.billing_details.email)">
                        <div class="pm-billing-row" v-if="pm.billing_details.name">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                <circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="1.8"/>
                            </svg>
                            {{ pm.billing_details.name }}
                        </div>
                        <div class="pm-billing-row" v-if="pm.billing_details.email">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                <path d="M22 6l-10 7L2 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            </svg>
                            {{ pm.billing_details.email }}
                        </div>
                        <div class="pm-billing-row"
                            v-if="pm.billing_details.address && (pm.billing_details.address.line1 || pm.billing_details.address.city)">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" stroke-width="1.8"/>
                                <circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="1.8"/>
                            </svg>
                            {{ [pm.billing_details.address.line1, pm.billing_details.address.city, pm.billing_details.address.postal_code, pm.billing_details.address.country].filter(Boolean).join(', ') }}
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="pm-card__footer" v-if="pm.id !== paymentMethods.default_source">
                        <button class="pm-action-btn pm-action-btn--default"
                            @click="markAsDefaultCard(pm.id)"
                            :disabled="isDisabled">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            Set as Default
                        </button>
                        <button class="pm-action-btn pm-action-btn--delete"
                            @click="confirmDelete(pm.id)"
                            :disabled="isDisabled">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            </svg>
                            Remove
                        </button>
                    </div>

                    <!-- Default card — can't delete label -->
                    <div class="pm-card__default-note" v-else>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                        This is your default card and will be used for billing
                    </div>
                </div>
            </div>

            <!-- Stripe security note -->
            <div class="pm-stripe-note" v-if="!isLoading">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
                Payments secured by Stripe. Your card details are never stored on our servers.
            </div>

            <!-- Delete confirm modal -->
            <div class="pm-modal-overlay" v-if="showDeleteModal" @click.self="showDeleteModal = false">
                <div class="pm-modal">
                    <div class="pm-modal__title">Remove Card?</div>
                    <div class="pm-modal__body">This card will be removed from your account. This action cannot be undone.</div>
                    <div class="pm-modal__footer">
                        <button class="pm-modal__cancel" @click="showDeleteModal = false" :disabled="isDisabled">Cancel</button>
                        <button class="pm-modal__confirm" @click="deletePaymentMethod(cardToDelete)" :disabled="isDisabled">
                            <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
                            <span v-else>Remove</span>
                        </button>
                    </div>
                </div>
            </div>

            <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
        </div>
    </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import KidsService from "../../services/KidsService";

export default {
    name: 'Payment Methods',
    components: { KidHeader, KidFooter },
    computed: {
        sortedPaymentMethods() {
            if (!this.paymentMethods.data) return [];
            const defaultId = this.paymentMethods.default_source;
            return [...this.paymentMethods.data].sort((a, b) => {
                if (a.id === defaultId) return -1;
                if (b.id === defaultId) return 1;
                return 0;
            });
        },
    },
    data() {
        return {
            profileId: this.$route.query.profileId || this.$route.query.profile_id,
            serialNumber: this.$route.query.serialNumber,
            isDisabled: false,
            isLoading: false,
            isAddingCard: false,
            paymentMethods: [],
            showDeleteModal: false,
            cardToDelete: null,
            showSuccessBanner: false,
            showCancelBanner: false,
        }
    },
    methods: {
        async getPaymentMethods() {
            this.isLoading = true;
            await KidsService.getPaymentMethods()
            .then((response) => {
                this.paymentMethods = response.data.data;
            }).catch((e) => {
                this.$toast.error(e.response.data.respones_msg);
            }).finally(() => {
                this.isLoading = false;
            });
        },
        toSentenceCase(value) {
            if (!value) return value;
            return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        },
        getCardBrandLogo(brand) {
            if (!brand) return null;
            const b = brand?.toLowerCase();
            const logos = {
                visa: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 780 500"><rect width="780" height="500" rx="40" fill="#1a1f71"/><path d="M293 340l33-194h53L346 340h-53zm243-189c-10-4-27-8-47-8-52 0-88 26-89 64-1 28 26 44 46 53 20 10 27 16 27 25 0 13-16 19-31 19-21 0-32-3-49-10l-7-3-7 42c12 5 34 10 57 10 55 0 90-26 91-66 0-22-14-39-44-53-18-9-30-15-30-24 0-8 10-17 31-17 18 0 31 4 41 8l5 2 7-42zm135-5h-40c-13 0-22 4-28 17l-79 177h56s9-24 11-29h68c2 7 6 29 6 29h49L671 146zm-66 122c4-11 20-53 20-53s4-11 7-18l3 16s9 43 11 55h-41zm-338-122l-52 132-6-27c-10-29-40-61-74-77l47 175h57l85-203h-57z" fill="#fff"/><path d="M167 146h-86l-1 4c67 16 111 55 129 102l-18-88c-3-13-12-17-24-18z" fill="#f9a533"/></svg>`,

                mastercard: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 780 500"><rect width="780" height="500" rx="40" fill="#fff"/><circle cx="295" cy="250" r="165" fill="#eb001b"/><circle cx="485" cy="250" r="165" fill="#f79e1b"/><path d="M390 114a165 165 0 0 1 0 272 165 165 0 0 1 0-272z" fill="#ff5f00"/></svg>`,

                amex: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 780 500"><rect width="780" height="500" rx="40" fill="#2557d6"/><text x="390" y="310" text-anchor="middle" font-family="Arial Black,sans-serif" font-size="140" font-weight="900" fill="#fff">AMEX</text></svg>`,

                discover: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 780 500"><rect width="780" height="500" rx="40" fill="#fff"/><text x="220" y="310" font-family="Arial,sans-serif" font-size="100" font-weight="700" fill="#231f20">DISCOVER</text><circle cx="620" cy="250" r="130" fill="#f76f20"/></svg>`,

                unionpay: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 780 500"><rect width="780" height="500" rx="40" fill="#e21836"/><rect x="260" y="0" width="260" height="500" fill="#fff"/><rect x="520" y="0" width="260" height="500" fill="#00447c"/></svg>`,

                jcb: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 780 500"><rect width="780" height="500" rx="40" fill="#fff"/><rect x="160" y="80" width="140" height="340" rx="30" fill="#003087"/><rect x="320" y="80" width="140" height="340" rx="30" fill="#e21836"/><rect x="480" y="80" width="140" height="340" rx="30" fill="#007b40"/></svg>`,
            };
            return logos[b] || null;
        },
        getPaymentMethodName(pm) {
            switch (pm.type) {
                case 'card':
                    return `${this.toSentenceCase(pm.card?.brand)} •••• ${pm.card?.last4}`;
                case 'link':
                    return `Stripe Link`;
                case 'paypal':
                    return `PayPal`;
                case 'sepa_debit':
                    return `SEPA •••• ${pm.sepa_debit?.last4 || ''}`;
                case 'bacs_debit':
                    return `BACS •••• ${pm.bacs_debit?.last4 || ''}`;
                case 'apple_pay':
                    return `Apple Pay`;
                case 'google_pay':
                    return `Google Pay`;
                default:
                    return this.toSentenceCase(pm.type?.replace(/_/g, ' ') || 'Payment Method');
            }
        },

        getPaymentMethodSubtitle(pm) {
            switch (pm.type) {
                case 'card':
                    return `Expires ${pm.card?.exp_month}/${pm.card?.exp_year}`;
                case 'link':
                    return pm.link?.email || pm.billing_details?.email || 'Stripe Link account';
                case 'paypal':
                    return pm.paypal?.payer_email || pm.billing_details?.email || 'PayPal account';
                case 'sepa_debit':
                    return `${pm.sepa_debit?.bank_code || ''} · ${pm.sepa_debit?.country || ''}`;
                case 'bacs_debit':
                    return `Sort code: ${pm.bacs_debit?.sort_code || ''}`;
                default:
                    return pm.billing_details?.email || '';
            }
        },
        async addPaymentMethod() {
            this.isAddingCard = true;
            try {
                const response = await KidsService.createSetupSession({
                    profileId: this.profileId
                });
                const url = response.data.data?.url;
                if (url) {
                    window.location.href = url;
                } else {
                    this.$toast.error('Failed to create setup session');
                }
            } catch (e) {
                this.$toast.error(e.response?.data?.respones_msg || 'Failed to open card setup');
            } finally {
                this.isAddingCard = false;
            }
        },
        async markAsDefaultCard(id) {
            this.isDisabled = true;
            let data = { id: id, isKids: true };
            await KidsService.markAsDefaultCard(data)
            .then((response) => {
                this.getPaymentMethods();
                this.$toast.success(response.data.respones_msg);
            }).catch((e) => {
                this.$toast.error(e.response.data.respones_msg);
            }).finally(() => {
                this.isDisabled = false;
            });
        },
        confirmDelete(id) {
            this.cardToDelete = id;
            this.showDeleteModal = true;
        },
        async deletePaymentMethod(id) {
            this.isDisabled = true;
            let data = { id: id, isKids: true };
            await KidsService.deletePaymentMethod(data)
            .then((response) => {
                this.showDeleteModal = false;
                this.cardToDelete = null;
                this.getPaymentMethods();
                this.$toast.success(response.data.respones_msg);
            }).catch((e) => {
                this.$toast.error(e.response.data.respones_msg);
            }).finally(() => {
                this.isDisabled = false;
            });
        },
        checkStripeReturn() {
            const query = this.$route.query;
            if (query.setup === 'success') {
                this.showSuccessBanner = true;
                this.$router.replace({ query: { ...query, setup: undefined } });
                setTimeout(() => { this.showSuccessBanner = false; }, 5000);
            } else if (query.setup === 'cancel') {
                this.showCancelBanner = true;
                this.$router.replace({ query: { ...query, setup: undefined } });
                setTimeout(() => { this.showCancelBanner = false; }, 5000);
            }
        },
    },
    beforeMount() {
        this.getPaymentMethods();
        this.checkStripeReturn();
    },
}
</script>

<style scoped>
/* ── Back nav ── */
.req-back { padding: 12px 14px 8px; width: 100%; text-align: left; }
.req-back__link { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #374151; text-decoration: none; -webkit-tap-highlight-color: transparent; }

/* ── Page header ── */
.pm-page-header { width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 4px 14px 14px; }
.pm-page-header__title { font-size: 20px; font-weight: 700; color: #111; margin: 0 0 2px; }
.pm-page-header__sub { font-size: 12px; color: #6b7280; margin: 0; }

/* ── Add button ── */
.pm-add-btn {
    display: inline-flex; align-items: center; gap: 5px;
    height: 36px; padding: 0 14px;
    background: #2563eb; color: #fff;
    border: none; border-radius: 10px;
    font-size: 13px; font-weight: 600;
    cursor: pointer; flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
}
.pm-add-btn:active { opacity: 0.85; }
.pm-add-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.pm-add-btn--full { width: calc(100% - 28px); margin: 16px 14px 0; justify-content: center; height: 44px; border-radius: 12px; }

/* ── Banners ── */
.pm-success-banner, .pm-cancel-banner {
    display: flex; align-items: center; gap: 8px;
    margin: 0 14px 10px; padding: 11px 12px;
    border-radius: 12px; font-size: 13px; font-weight: 500;
}
.pm-success-banner { background: #f0fdf4; border: 0.5px solid #bbf7d0; color: #16a34a; }
.pm-cancel-banner { background: #fffbeb; border: 0.5px solid #fde68a; color: #92400e; }
.pm-success-banner__close {
    margin-left: auto; background: none; border: none;
    cursor: pointer; color: inherit; padding: 0; display: flex; align-items: center;
}

/* ── Loader ── */
.pm-loader { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 48px 14px; color: #6b7280; font-size: 13px; }

/* ── Empty ── */
.pm-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 48px 14px; text-align: center; }
.pm-empty__icon { width: 72px; height: 72px; border-radius: 50%; background: #f3f4f6; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.pm-empty__title { font-size: 16px; font-weight: 700; color: #111; }
.pm-empty__sub { font-size: 13px; color: #6b7280; }

/* ── Cards list ── */
.pm-list { display: flex; flex-direction: column; gap: 10px; padding: 0 14px; width: 100%;
    text-align: left; }

/* ── Payment card ── */
.pm-card {
    background: #fff; border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    overflow: hidden;
}
.pm-card--default {
    border: 1px solid #bfdbfe;
    border-left: 4px solid #2563eb;
    background: linear-gradient(135deg, #f5f9ff 0%, #eff6ff 100%);
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
}
.pm-default-ribbon {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #2563eb;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    padding: 5px 14px;
}
.pm-card--default .pm-card__header,
.pm-card--default .pm-card__billing,
.pm-card--default .pm-card__default-note {
    background: transparent;
}
/* Card header */
.pm-card__header {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 14px 12px;
    border-bottom: 0.5px solid #f3f4f6;
}
.pm-card__brand-icon {
    width: 52px; height: 34px;
    border-radius: 6px; background: #f9fafb;
    border: 0.5px solid #e5e7eb;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; overflow: hidden; padding: 4px;
}
.pm-card__brand-svg {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
}
.pm-card__brand-svg svg {
    width: 100%; height: 100%;
    border-radius: 4px;
}
.pm-card__brand-img { width: 100%; height: 100%; object-fit: contain; }
.pm-card__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.pm-card__name-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pm-card__name { font-size: 14px; font-weight: 700; color: #111; font-family: monospace; letter-spacing: 0.5px; }
.pm-card__expiry { font-size: 12px; color: #6b7280; }

.pm-default-badge {
    display: inline-flex; align-items: center; gap: 3px;
    font-size: 10px; font-weight: 700;
    background: #dbeafe; color: #1e40af;
    padding: 2px 7px; border-radius: 20px;
}

/* Billing details */
.pm-card__billing {
    padding: 10px 14px 12px;
    display: flex; flex-direction: column; gap: 5px;
    border-bottom: 0.5px solid #f3f4f6;
    background: #fafafa;
}
.pm-billing-row {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; color: #6b7280;
}

/* Footer actions */
.pm-card__footer {
    display: flex; gap: 8px;
    padding: 10px 14px;
}
.pm-action-btn {
    display: inline-flex; align-items: center; gap: 5px;
    height: 34px; padding: 0 12px; border-radius: 8px;
    font-size: 12px; font-weight: 600;
    cursor: pointer; border: 0.5px solid #e5e7eb;
    -webkit-tap-highlight-color: transparent;
}
.pm-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pm-action-btn:active:not(:disabled) { opacity: 0.75; }
.pm-action-btn--default { background: #eff6ff; color: #2563eb; border-color: #bfdbfe; }
.pm-action-btn--delete { background: #fff5f5; color: #dc2626; border-color: #fecaca; }

/* Default note */
.pm-card__default-note {
    display: flex; align-items: center; gap: 6px;
    padding: 10px 14px;
    font-size: 12px; color: #6b7280;
    background: #f9fafb;
}

/* ── Stripe note ── */
.pm-stripe-note {
    display: flex; align-items: center; justify-content: center;
    gap: 5px; font-size: 11px; color: #9ca3af;
    padding: 16px 14px 24px;
    text-align: center;
}

/* ── Delete modal ── */
.pm-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.45);
    z-index: 300; display: flex; align-items: flex-end; justify-content: center;
}
.pm-modal {
    background: #fff; border-radius: 20px 20px 0 0;
    width: 100%; max-width: 575px;
    padding: 20px 16px 32px;
    animation: slideUp 0.25s ease;
}
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.pm-modal__title { font-size: 17px; font-weight: 700; color: #111; margin-bottom: 8px; }
.pm-modal__body { font-size: 13px; color: #6b7280; line-height: 1.5; margin-bottom: 20px; }
.pm-modal__footer { display: flex; gap: 10px; }
.pm-modal__cancel {
    flex: 1; height: 44px; border-radius: 12px;
    border: 0.5px solid #e5e7eb; background: #f3f4f6;
    font-size: 14px; font-weight: 600; color: #374151; cursor: pointer;
}
.pm-modal__confirm {
    flex: 1; height: 44px; border-radius: 12px; border: none;
    background: #dc2626; color: #fff;
    font-size: 14px; font-weight: 600; cursor: pointer;
}
.pm-modal__confirm:disabled, .pm-modal__cancel:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 575px) {
    .pm-modal { padding-bottom: calc(60px + env(safe-area-inset-bottom, 16px)); }
}

button:focus { outline: none; }
</style>